import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { OrganicDateFilters } from "src/dashboards/organicMedia/components/Tabs/FacebookContentReport";
import OrganicDateRangePicker from "src/components/common/OrganicDateRangePicker/OrganicDateRangePicker";
import {
  DashboardErrorState,
  DashboardNarrativePanel,
  DashboardExportButton,
  DashboardSectionHeader,
  toggleTableSort,
  type SortDirection,
  type DefinitionTooltipInfo,
} from "src/components/common";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { renderUniqueDashboardKpiLucideIcons } from "src/utils/dashboardKpiLucideIcon";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { useDashboardPdfRowOverride } from "src/hooks/UseDashboardPdfTableExpansion";
import { fetchAllPagesForExport } from "src/utils/pdfExport/fetchAllPagesForExport";
import { getNumberLocale } from "src/utils/numberFormat";
import {
  type MetaAdsAdItem,
  type MetaAdsAdsetItem,
  type MetaAdsAdSort,
  type MetaAdsAdsetSort,
  type MetaAdsCampaignItem,
  type MetaAdsCampaignSort,
  metaAdsDashboardService,
  type MetaAdsKpiItem,
  type MetaAdsPaginationMeta,
  type MetaAdsPerformanceTrendItem,
  type MetaAdsPlatformItem,
  type MetaAdsPostTypeItem,
  type MetaAdsRoasByCampaignItem,
} from "src/services/MetaAdsDashboardAPI";
import {
  extractCurrencyPrefixFromKpiItems,
  formatPaidMediaMoney,
  formatPaidMediaMoneyShort,
} from "../../utils/PaidMediaCurrencyFormat";
import { downloadCsv } from "../../utils/PaidMediaCsvExport";
import ConversionFunnelPanel from "../metaAds/ConversionFunnelPanel";
import MetaKpiGrid from "../metaAds/MetaKpiGrid";
import MetaChartsRow from "../metaAds/MetaChartsRow";
import MetaPerformanceTrendsPanel from "../metaAds/MetaPerformanceTrendsPanel";
import MetaCampaignsTable from "../metaAds/MetaCampaignsTable";
import MetaAdSetsTable from "../metaAds/MetaAdSetsTable";
import MetaAdsTable from "../metaAds/MetaAdsTable";
import MetaAlertsPanel from "../metaAds/MetaAlertsPanel";
import {
  META_KPI_ICON_LABELS,
  META_KPI_I18N_KEY,
  type MetaKpiMetricId,
  type MetaKpiTooltipSuffix,
  formatMetaKpiTitle,
  matchTitleTooltipSuffix,
} from "../metaAds/MetaKpiConfig";
import {
  META_CHART_METRIC_COLORS,
  type MetaGroupedChartDatum,
  type MetaGroupedChartMetricKey,
  type TrendGranularity,
  type TrendMetricKey,
} from "../metaAds/MetaChartConfig";
import {
  AD_SORT_FIELD,
  ADSET_SORT_FIELD,
  CAMPAIGN_SORT_FIELD,
  DEFAULT_ALERT_RULES,
  EMPTY_API_VALUE,
  EMPTY_PAGINATION_META,
  SEARCH_DEBOUNCE_MS,
  TABLE_PAGE_SIZE,
  TREND_PAGE_SIZE,
  computeAlertMetricValue,
  mapAdApiItem,
  mapAdsetApiItem,
  mapCampaignApiItem,
  toApiSort,
  toFiniteNumber,
  type ActiveOverviewTab,
  type AdSetSortKey,
  type AdSortKey,
  type AlertRule,
  type CampaignSortKey,
  type CampaignSummary,
  type LiveAdRow,
} from "../metaAds/MetaTableConfig";

const mapPerformanceTrendChartRow = (item: MetaAdsPerformanceTrendItem) => ({
  date: item.date,
  spend: Number(toFiniteNumber(item.total_spend).toFixed(2)),
  spendFormatted: item.total_spendFormattedValue,
  impressions: toFiniteNumber(item.impressions),
  clicks: toFiniteNumber(item.clicks),
  addToCart: toFiniteNumber(item.add_to_cart),
  conversions: toFiniteNumber(item.purchases),
});

const normalizeMetaGroupedChartData = <T extends Record<string, string | number>>(
  rows: T[],
  labelKey: keyof T
): MetaGroupedChartDatum[] => {
  const metricKeys: MetaGroupedChartMetricKey[] = [
    "spend",
    "impressions",
    "conversions",
    "ctr",
  ];
  const maxByMetric = metricKeys.reduce<Record<MetaGroupedChartMetricKey, number>>(
    (accumulator, key) => {
      accumulator[key] = Math.max(
        ...rows.map(row => toFiniteNumber(row[key])),
        1
      );
      return accumulator;
    },
    { spend: 1, impressions: 1, conversions: 1, ctr: 1 }
  );

  return rows.map(row => {
    const spend = toFiniteNumber(row.spend);
    const impressions = toFiniteNumber(row.impressions);
    const conversions = toFiniteNumber(row.conversions);
    const ctr = toFiniteNumber(row.ctr);

    return {
      label: String(row[labelKey] ?? "Unknown"),
      spend,
      impressions,
      conversions,
      ctr,
      spendValue: (spend / maxByMetric.spend) * 100,
      impressionsValue: (impressions / maxByMetric.impressions) * 100,
      conversionsValue: (conversions / maxByMetric.conversions) * 100,
      ctrValue: (ctr / maxByMetric.ctr) * 100,
    };
  });
};

const MetaAdsReport: FC<{ dateFilters: OrganicDateFilters }> = ({
  dateFilters,
}) => {
  const { i18n, t } = useTranslation();
  const {
    appliedStartDate,
    appliedEndDate,
    draftStartDate,
    draftEndDate,
    onApplyDates,
  } = dateFilters;
  const pdfContainerRef = useRef<HTMLElement>(null);
  const [isPdfCapturing, setIsPdfCapturing] = useState(false);
  const [searchCampaigns, setSearchCampaigns] = useState("");
  const [searchAds, setSearchAds] = useState("");
  const [searchAdSets, setSearchAdSets] = useState("");
  const [debouncedSearchCampaigns, setDebouncedSearchCampaigns] = useState("");
  const [debouncedSearchAdSets, setDebouncedSearchAdSets] = useState("");
  const [debouncedSearchAds, setDebouncedSearchAds] = useState("");
  const [currentCampaignPage, setCurrentCampaignPage] = useState(1);
  const [currentAdSetPage, setCurrentAdSetPage] = useState(1);
  const [currentAdsPage, setCurrentAdsPage] = useState(1);
  const [currentTriggeredAlertsPage, setCurrentTriggeredAlertsPage] = useState(1);
  const [currentTrendPage, setCurrentTrendPage] = useState(1);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(() => new Set());
  const [isKpiLoading, setIsKpiLoading] = useState(true);
  const [isSegmentLoading, setIsSegmentLoading] = useState(true);
  const [isCampaignLoading, setIsCampaignLoading] = useState(true);
  const [isAdSetLoading, setIsAdSetLoading] = useState(false);
  const [isAdsLoading, setIsAdsLoading] = useState(false);
  const [kpiApiError, setKpiApiError] = useState("");
  const [liveKpis, setLiveKpis] = useState<MetaAdsKpiItem[]>([]);
  const [campaignApiError, setCampaignApiError] = useState("");
  const [liveCampaigns, setLiveCampaigns] = useState<MetaAdsCampaignItem[]>([]);
  const [campaignPaginationMeta, setCampaignPaginationMeta] =
    useState<MetaAdsPaginationMeta>(EMPTY_PAGINATION_META);
  const [liveRoasByCampaign, setLiveRoasByCampaign] = useState<MetaAdsRoasByCampaignItem[]>([]);
  const [adSetApiError, setAdSetApiError] = useState("");
  const [liveAdsets, setLiveAdsets] = useState<MetaAdsAdsetItem[]>([]);
  const [adSetPaginationMeta, setAdSetPaginationMeta] =
    useState<MetaAdsPaginationMeta>(EMPTY_PAGINATION_META);
  const [adsApiError, setAdsApiError] = useState("");
  const [liveAds, setLiveAds] = useState<MetaAdsAdItem[]>([]);
  const [adsPaginationMeta, setAdsPaginationMeta] =
    useState<MetaAdsPaginationMeta>(EMPTY_PAGINATION_META);
  const [segmentApiError, setSegmentApiError] = useState("");
  const [livePlatforms, setLivePlatforms] = useState<MetaAdsPlatformItem[]>([]);
  const [livePostTypes, setLivePostTypes] = useState<MetaAdsPostTypeItem[]>([]);
  const [livePerformanceTrends, setLivePerformanceTrends] = useState<
    MetaAdsPerformanceTrendItem[]
  >([]);
  const [livePerformanceTrendsMonthly, setLivePerformanceTrendsMonthly] = useState<
    MetaAdsPerformanceTrendItem[]
  >([]);
  const [isTrendsLoading, setIsTrendsLoading] = useState(true);
  const [trendsApiError, setTrendsApiError] = useState("");
  const [activeTab, setActiveTab] = useState<ActiveOverviewTab>("campaigns");
  const [isExporting, setIsExporting] = useState(false);
  const [alerts, setAlerts] = useState<AlertRule[]>(DEFAULT_ALERT_RULES);
  const [campaignSort, setCampaignSort] = useState<{
    key: CampaignSortKey;
    direction: SortDirection;
  } | null>({ key: "spend", direction: "desc" });
  const [adSetSort, setAdSetSort] = useState<{
    key: AdSetSortKey;
    direction: SortDirection;
  } | null>(null);
  const [adsSort, setAdsSort] = useState<{
    key: AdSortKey;
    direction: SortDirection;
  } | null>(null);
  const [activeTrendMetrics, setActiveTrendMetrics] = useState<TrendMetricKey[]>([
    "spend",
    "impressions",
    "clicks",
    "addToCart",
    "conversions",
  ]);
  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>("daily");
  const apiLang = getApiLangFromI18n(i18n);

  const trendMetrics = useMemo(
    () =>
      (["spend", "impressions", "clicks", "addToCart", "conversions"] as const).map(key => ({
        key,
        label: t(`metaAdsReport.metrics.${key}`),
        color: META_CHART_METRIC_COLORS[key],
      })),
    [t]
  );

  const formatChartAxisCompactLocalized = useCallback(
    (value: unknown) => {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return "";
      }
      return new Intl.NumberFormat(getNumberLocale(i18n.language), {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(n);
    },
    [i18n.language]
  );

  const formatDateShortChart = useCallback(
    (value: string) => {
      const iso = value.length >= 10 ? value.slice(0, 10) : value;
      const monthOnly = value.length === 7 ? value : iso.slice(0, 7);
      const parsedDaily = dayjs(iso, "YYYY-MM-DD", true);
      const parsedMonthly = dayjs(monthOnly, "YYYY-MM", true);

      if (trendGranularity === "monthly" && parsedMonthly.isValid()) {
        if (i18n.language?.startsWith("ar")) {
          return new Intl.DateTimeFormat(getNumberLocale(i18n.language), {
            month: "short",
            year: "2-digit",
          }).format(parsedMonthly.toDate());
        }
        return parsedMonthly.format("MMM YY");
      }

      if (parsedDaily.isValid()) {
        if (i18n.language?.startsWith("ar")) {
          return new Intl.DateTimeFormat(getNumberLocale(i18n.language), {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }).format(parsedDaily.toDate());
        }
        return parsedDaily.format("DD-MM-YYYY");
      }

      return value.length >= 10 ? value.slice(5, 10) : value;
    },
    [i18n.language, trendGranularity]
  );

  const barSeriesLabels = useMemo(
    () => ({
      spend: t("metaAdsReport.metrics.spend"),
      conversions: t("metaAdsReport.metrics.conversions"),
      impressions: t("metaAdsReport.metrics.impressions"),
      ctr: t("metaAdsReport.metrics.ctr"),
    }),
    [t]
  );

  const groupedBarLegendItems = useMemo(
    () =>
      [
        { key: "spend" as const, label: barSeriesLabels.spend },
        { key: "impressions" as const, label: barSeriesLabels.impressions },
        { key: "conversions" as const, label: barSeriesLabels.conversions },
        { key: "ctr" as const, label: barSeriesLabels.ctr },
      ] as const,
    [barSeriesLabels]
  );

  const metaSectionHelp = useCallback(
    (key: string): DefinitionTooltipInfo => ({
      description: t(`metaAdsReport.sectionHelp.${key}.description`),
    }),
    [t]
  );

  const alertRuleLabel = useCallback(
    (id: AlertRule["id"]) => {
      if (id === "high-cpa") {
        return t("metaAdsReport.alerts.ruleHighCpa");
      }
      if (id === "low-roas") {
        return t("metaAdsReport.alerts.ruleLowRoas");
      }
      return t("metaAdsReport.alerts.ruleHighFrequency");
    },
    [t]
  );
  const isLoading =
    isKpiLoading ||
    isSegmentLoading ||
    isCampaignLoading ||
    isTrendsLoading;
  const isTableLoading =
    (activeTab === "adsets" && isAdSetLoading) ||
    (activeTab === "ads" && isAdsLoading);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearchCampaigns(searchCampaigns.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timer);
  }, [searchCampaigns]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearchAdSets(searchAdSets.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timer);
  }, [searchAdSets]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearchAds(searchAds.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timer);
  }, [searchAds]);

  useEffect(() => {
    setCurrentCampaignPage(1);
    setCurrentAdSetPage(1);
    setCurrentAdsPage(1);
    setCurrentTriggeredAlertsPage(1);
  }, [
    debouncedSearchAds,
    debouncedSearchAdSets,
    debouncedSearchCampaigns,
    appliedStartDate,
    appliedEndDate,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadKpis = async () => {
      try {
        setIsKpiLoading(true);
        setKpiApiError("");
        const response = await metaAdsDashboardService.fetchDashboardKPI({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          lang: apiLang,
        });

        if (isMounted) {
          setLiveKpis(response?.kpis ?? []);
        }
      } catch (error) {
        if (isMounted) {
          setLiveKpis([]);
          setKpiApiError(
            error instanceof Error ? error.message : t("metaAdsReport.errors.kpiLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsKpiLoading(false);
        }
      }
    };

    if (appliedStartDate <= appliedEndDate) {
      loadKpis();
    } else {
      setLiveKpis([]);
      setKpiApiError("");
      setIsKpiLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [apiLang, appliedEndDate, appliedStartDate, t]);

  useEffect(() => {
    let isMounted = true;

    const loadSegments = async () => {
      setIsSegmentLoading(true);
      setSegmentApiError("");
      try {
        const [platformsResult, postTypesResult] = await Promise.allSettled([
          metaAdsDashboardService.fetchPlatformsKPI({
            start_date: appliedStartDate,
            end_date: appliedEndDate,
            lang: apiLang,
          }),
          metaAdsDashboardService.fetchPostTypesKPI({
            start_date: appliedStartDate,
            end_date: appliedEndDate,
            lang: apiLang,
          }),
        ]);

        if (!isMounted) {
          return;
        }

        setLivePlatforms(
          platformsResult.status === "fulfilled"
            ? platformsResult.value?.platforms ?? []
            : []
        );
        setLivePostTypes(
          postTypesResult.status === "fulfilled" ? postTypesResult.value?.post_types ?? [] : []
        );

        const failedResult = [platformsResult, postTypesResult].find(
          result => result.status === "rejected"
        );

        if (failedResult?.status === "rejected") {
          setSegmentApiError(
            failedResult.reason instanceof Error
              ? failedResult.reason.message
              : t("metaAdsReport.errors.segmentLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsSegmentLoading(false);
        }
      }
    };

    if (appliedStartDate <= appliedEndDate) {
      loadSegments();
    } else {
      setLivePlatforms([]);
      setLivePostTypes([]);
      setSegmentApiError("");
      setIsSegmentLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [apiLang, appliedEndDate, appliedStartDate, t]);

  useEffect(() => {
    let isMounted = true;

    const loadCampaigns = async () => {
      try {
        setIsCampaignLoading(true);
        setCampaignApiError("");
        const sort = toApiSort<MetaAdsCampaignSort>(
          campaignSort,
          CAMPAIGN_SORT_FIELD,
          "total_spend_desc"
        );
        const response = await metaAdsDashboardService.fetchCampaignKPI({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          lang: apiLang,
          page: currentCampaignPage,
          page_size: TABLE_PAGE_SIZE,
          sort: sort ?? "total_spend_desc",
          search: debouncedSearchCampaigns || null,
          platform: null,
        });

        if (isMounted) {
          setLiveCampaigns(response?.campaigns ?? []);
          setCampaignPaginationMeta(response?.meta ?? EMPTY_PAGINATION_META);
        }
      } catch (error) {
        if (isMounted) {
          setLiveCampaigns([]);
          setCampaignPaginationMeta(EMPTY_PAGINATION_META);
          setCampaignApiError(
            error instanceof Error ? error.message : t("metaAdsReport.errors.campaignLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsCampaignLoading(false);
        }
      }
    };

    if (appliedStartDate <= appliedEndDate) {
      loadCampaigns();
    } else {
      setLiveCampaigns([]);
      setCampaignPaginationMeta(EMPTY_PAGINATION_META);
      setCampaignApiError("");
      setIsCampaignLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [
    apiLang,
    appliedEndDate,
    appliedStartDate,
    campaignSort,
    currentCampaignPage,
    debouncedSearchCampaigns,
    t,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadRoasChart = async () => {
      try {
        const response = await metaAdsDashboardService.fetchRoasByCampaign({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          lang: apiLang,
        });

        if (isMounted) {
          setLiveRoasByCampaign(response?.campaigns ?? []);
        }
      } catch {
        if (isMounted) {
          setLiveRoasByCampaign([]);
        }
      }
    };

    if (appliedStartDate <= appliedEndDate) {
      loadRoasChart();
    } else {
      setLiveRoasByCampaign([]);
    }

    return () => {
      isMounted = false;
    };
  }, [apiLang, appliedEndDate, appliedStartDate]);

  useEffect(() => {
    if (activeTab !== "adsets" || appliedStartDate > appliedEndDate) {
      return undefined;
    }

    let isMounted = true;

    const loadAdsets = async () => {
      try {
        setIsAdSetLoading(true);
        setAdSetApiError("");
        const sort = toApiSort<MetaAdsAdsetSort>(
          adSetSort,
          ADSET_SORT_FIELD,
          "total_spend_desc"
        );
        const response = await metaAdsDashboardService.fetchAdsetKPI({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          lang: apiLang,
          page: currentAdSetPage,
          page_size: TABLE_PAGE_SIZE,
          sort: sort ?? "total_spend_desc",
          search: debouncedSearchAdSets || null,
          platform: null,
        });

        if (isMounted) {
          setLiveAdsets(response?.adsets ?? []);
          setAdSetPaginationMeta(response?.meta ?? EMPTY_PAGINATION_META);
        }
      } catch (error) {
        if (isMounted) {
          setLiveAdsets([]);
          setAdSetPaginationMeta(EMPTY_PAGINATION_META);
          setAdSetApiError(
            error instanceof Error ? error.message : t("metaAdsReport.errors.adSetLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsAdSetLoading(false);
        }
      }
    };

    loadAdsets();

    return () => {
      isMounted = false;
    };
  }, [
    activeTab,
    adSetSort,
    apiLang,
    appliedEndDate,
    appliedStartDate,
    currentAdSetPage,
    debouncedSearchAdSets,
    t,
  ]);

  useEffect(() => {
    if (activeTab !== "ads" || appliedStartDate > appliedEndDate) {
      return undefined;
    }

    let isMounted = true;

    const loadAds = async () => {
      try {
        setIsAdsLoading(true);
        setAdsApiError("");
        const sort = toApiSort<MetaAdsAdSort>(adsSort, AD_SORT_FIELD, "total_spend_desc");
        const response = await metaAdsDashboardService.fetchAdKPI({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          lang: apiLang,
          page: currentAdsPage,
          page_size: TABLE_PAGE_SIZE,
          sort: sort ?? "total_spend_desc",
          search: debouncedSearchAds || null,
          platform: null,
        });

        if (isMounted) {
          setLiveAds(response?.ads ?? []);
          setAdsPaginationMeta(response?.meta ?? EMPTY_PAGINATION_META);
        }
      } catch (error) {
        if (isMounted) {
          setLiveAds([]);
          setAdsPaginationMeta(EMPTY_PAGINATION_META);
          setAdsApiError(
            error instanceof Error ? error.message : t("metaAdsReport.errors.adsLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsAdsLoading(false);
        }
      }
    };

    loadAds();

    return () => {
      isMounted = false;
    };
  }, [
    activeTab,
    adsSort,
    apiLang,
    appliedEndDate,
    appliedStartDate,
    currentAdsPage,
    debouncedSearchAds,
    t,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadTrends = async () => {
      try {
        setIsTrendsLoading(true);
        setTrendsApiError("");
        const trendsResponse = await metaAdsDashboardService.fetchPerformanceTrends({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          lang: apiLang,
        });

        if (isMounted) {
          setLivePerformanceTrends(trendsResponse?.performance_trends ?? []);
          setLivePerformanceTrendsMonthly(
            trendsResponse?.performance_trends_monthly ?? []
          );
        }
      } catch (error) {
        if (isMounted) {
          setLivePerformanceTrends([]);
          setLivePerformanceTrendsMonthly([]);
          setTrendsApiError(
            error instanceof Error
              ? error.message
              : t("metaAdsReport.errors.trendsLoad")
          );
        }
      } finally {
        if (isMounted) {
          setIsTrendsLoading(false);
        }
      }
    };

    if (appliedStartDate <= appliedEndDate) {
      loadTrends();
    } else {
      setLivePerformanceTrends([]);
      setLivePerformanceTrendsMonthly([]);
      setTrendsApiError("");
      setIsTrendsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [apiLang, appliedEndDate, appliedStartDate, t]);

  const dateRangeError =
    appliedStartDate > appliedEndDate ? t("metaAdsReport.errors.dateRange") : "";

  const narrativeData = useMemo(
    () => ({
      kpi: liveKpis,
      performance_trends: livePerformanceTrends,
      roas_by_campaign: liveRoasByCampaign,
      platforms: livePlatforms,
      post_types: livePostTypes,
      campaigns: liveCampaigns,
      adsets: liveAdsets,
      ads: liveAds,
    }),
    [liveKpis, livePerformanceTrends, liveRoasByCampaign, livePlatforms, livePostTypes, liveCampaigns, liveAdsets, liveAds]
  );

  const narrativeDateKey = `${appliedStartDate}|${appliedEndDate}`;
  const narrativePrevIsLoadingRef = useRef(true);
  const narrativeCommittedKeyRef = useRef("");
  const [lockedNarrativeData, setLockedNarrativeData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    narrativeCommittedKeyRef.current = "";
    setLockedNarrativeData(null);
  }, [appliedStartDate, appliedEndDate]);

  useEffect(() => {
    const wasLoading = narrativePrevIsLoadingRef.current;
    narrativePrevIsLoadingRef.current = isLoading;
    if (dateRangeError) {
      narrativeCommittedKeyRef.current = "";
      setLockedNarrativeData(null);
      return;
    }
    if (wasLoading && !isLoading && narrativeCommittedKeyRef.current !== narrativeDateKey) {
      narrativeCommittedKeyRef.current = narrativeDateKey;
      setLockedNarrativeData(narrativeData);
    }
  }, [isLoading, dateRangeError, narrativeDateKey, narrativeData]);

  const { narrative: metaNarrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "paid_media",
    tabKey: "meta_ads_report",
    startDate: appliedStartDate,
    endDate: appliedEndDate,
    data: lockedNarrativeData ?? {},
    enabled: lockedNarrativeData !== null && !dateRangeError,
    promptVersion: "v6",
  });

  const activeTrendSeries = useMemo(
    () =>
      trendGranularity === "monthly"
        ? livePerformanceTrendsMonthly
        : livePerformanceTrends,
    [livePerformanceTrends, livePerformanceTrendsMonthly, trendGranularity]
  );

  const trendData = useMemo(
    () =>
      [...activeTrendSeries]
        .map(mapPerformanceTrendChartRow)
        .sort((left, right) => left.date.localeCompare(right.date)),
    [activeTrendSeries]
  );

  const trendTotalPages = Math.max(1, Math.ceil(trendData.length / TREND_PAGE_SIZE));
  const paginatedTrendData = useMemo(
    () =>
      trendData.slice(
        (currentTrendPage - 1) * TREND_PAGE_SIZE,
        currentTrendPage * TREND_PAGE_SIZE
      ),
    [currentTrendPage, trendData]
  );

  useEffect(() => {
    setCurrentTrendPage(1);
  }, [appliedEndDate, appliedStartDate, trendGranularity]);

  useEffect(() => {
    setCurrentTrendPage(page => Math.min(page, trendTotalPages));
  }, [trendTotalPages]);

  const campaignRows = useMemo<CampaignSummary[]>(() => {
    return liveCampaigns.map(item => mapCampaignApiItem(item));
  }, [liveCampaigns]);

  const campaignTotalPages = Math.max(1, campaignPaginationMeta.total_pages || 1);
  const paginatedCampaigns = campaignRows;

  const funnelTotals = useMemo(() => {
    if (activeTrendSeries.length > 0) {
      return activeTrendSeries.reduce(
        (acc, row) => ({
          spend: acc.spend + toFiniteNumber(row.total_spend),
          impressions: acc.impressions + toFiniteNumber(row.impressions),
          clicks: acc.clicks + toFiniteNumber(row.clicks),
          addToCart: acc.addToCart + toFiniteNumber(row.add_to_cart),
          conversions: acc.conversions + toFiniteNumber(row.purchases),
        }),
        { spend: 0, impressions: 0, clicks: 0, addToCart: 0, conversions: 0 }
      );
    }

    const kpiSpendItem = liveKpis.find(x => {
      const id = x.id?.trim().toLowerCase() ?? "";
      const title = x.title?.trim().toLowerCase() ?? "";
      return id === "total-spend" || title === "total spend";
    });

    return {
      spend: toFiniteNumber(kpiSpendItem?.value),
      impressions: toFiniteNumber(
        liveKpis.find(x => x.id?.trim().toLowerCase() === "impressions")?.value
      ),
      clicks: toFiniteNumber(liveKpis.find(x => x.id?.trim().toLowerCase() === "clicks")?.value),
      addToCart: toFiniteNumber(
        liveKpis.find(x => x.id?.trim().toLowerCase() === "add-to-cart")?.value
      ),
      conversions: toFiniteNumber(
        liveKpis.find(x => {
          const id = x.id?.trim().toLowerCase() ?? "";
          return id === "conversions" || id === "purchases";
        })?.value
      ),
    };
  }, [activeTrendSeries, liveKpis]);

  const funnelSteps = useMemo(
    () =>
      [
        {
          label: t("metaAdsReport.metrics.spend"),
          value: Math.round(funnelTotals.spend),
          color: META_CHART_METRIC_COLORS.spend,
          kind: "currency" as const,
        },
        {
          label: t("metaAdsReport.metrics.impressions"),
          value: funnelTotals.impressions,
          color: META_CHART_METRIC_COLORS.impressions,
          kind: "count" as const,
        },
        {
          label: t("metaAdsReport.metrics.clicks"),
          value: Math.round(funnelTotals.clicks),
          color: META_CHART_METRIC_COLORS.clicks,
          kind: "count" as const,
        },
        {
          label: t("metaAdsReport.metrics.addToCart"),
          value: Math.round(funnelTotals.addToCart),
          color: META_CHART_METRIC_COLORS.addToCart,
          kind: "count" as const,
        },
        {
          label: t("metaAdsReport.metrics.conversions"),
          value: funnelTotals.conversions,
          color: META_CHART_METRIC_COLORS.conversions,
          kind: "count" as const,
        },
      ].sort((a, b) => b.value - a.value),
    [funnelTotals, t]
  );

  const campaignBarData = useMemo(
    () =>
      [...liveRoasByCampaign]
        .map(item => ({
          name: item.campaign_name.trim() || "Unknown",
          roas: Number(toFiniteNumber(item.roas).toFixed(2)),
        }))
        .sort((a, b) => b.roas - a.roas),
    [liveRoasByCampaign]
  );

  const platformComparisonData = useMemo(
    () => {
      const rows = livePlatforms.map(item => ({
        platform: item.platform_name?.trim() || "Unknown",
        spend: Number(toFiniteNumber(item.total_spend).toFixed(0)),
        conversions: toFiniteNumber(item.total_purchases),
        impressions: toFiniteNumber(item.impressions),
        ctr: toFiniteNumber(item.ctr),
      }));
      return normalizeMetaGroupedChartData(rows, "platform");
    },
    [livePlatforms]
  );

  const postTypeData = useMemo(
    () => {
      const rows = livePostTypes.map(item => ({
        type: item.post_type?.trim() || "Unknown",
        spend: Number(toFiniteNumber(item.total_spend).toFixed(0)),
        impressions: toFiniteNumber(item.impressions),
        conversions: toFiniteNumber(item.purchases),
        ctr: toFiniteNumber(item.ctr),
      }));
      return normalizeMetaGroupedChartData(rows, "type");
    },
    [livePostTypes]
  );

  const triggeredAlerts = useMemo(() => {
    return alerts
      .filter(alert => alert.enabled)
      .flatMap(alert =>
        campaignRows.flatMap(campaign => {
          const metricValue = computeAlertMetricValue(alert, campaign);

          const isTriggered =
            alert.operator === ">"
              ? metricValue > alert.threshold
              : metricValue < alert.threshold;

          return isTriggered
            ? [
              {
                id: `${alert.id}-${campaign.id}`,
                ruleId: alert.id,
                metric: alert.metric,
                campaignName: campaign.campaignName,
                value: metricValue,
              },
            ]
            : [];
        })
      );
  }, [alerts, campaignRows]);
  const triggeredAlertsTotalPages = Math.max(1, Math.ceil(triggeredAlerts.length / 10));
  const paginatedTriggeredAlerts = triggeredAlerts.slice(
    (currentTriggeredAlertsPage - 1) * 10,
    currentTriggeredAlertsPage * 10
  );

  const budgetRecommendations = useMemo(() => {
    const sorted = [...campaignRows].sort((left, right) => (right.roas ?? -Infinity) - (left.roas ?? -Infinity));
    return {
      increase: sorted.slice(0, 2),
      reduce: sorted.slice(-2).reverse(),
    };
  }, [campaignRows]);

  const adSetRows = useMemo(
    () => liveAdsets.map((item, index) => mapAdsetApiItem(item, index)),
    [liveAdsets]
  );

  const paginatedAdSets = adSetRows;
  const adSetTotalPages = Math.max(1, adSetPaginationMeta.total_pages || 1);

  const adRows = useMemo<LiveAdRow[]>(
    () => liveAds.map((item, index) => mapAdApiItem(item, index)),
    [liveAds]
  );

  const paginatedAds = adRows;
  const adsTotalPages = Math.max(1, adsPaginationMeta.total_pages || 1);

  const {
    rows: exportCampaignRows,
    onBeforeCapture: onBeforeCampaignPdfCapture,
    onAfterCapture: onAfterCampaignPdfCapture,
  } = useDashboardPdfRowOverride(paginatedCampaigns, async () => {
    if (activeTab !== "campaigns") {
      return paginatedCampaigns;
    }
    return fetchAllPagesForExport({
      pageSize: TABLE_PAGE_SIZE,
      fetchPage: async (page, pageSize) => {
        const sort = toApiSort<MetaAdsCampaignSort>(
          campaignSort,
          CAMPAIGN_SORT_FIELD,
          "total_spend_desc"
        );
        const response = await metaAdsDashboardService.fetchCampaignKPI({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          lang: apiLang,
          page,
          page_size: pageSize,
          sort: sort ?? "total_spend_desc",
          search: debouncedSearchCampaigns || null,
          platform: null,
        });
        return {
          rows: (response?.campaigns ?? []).map(item => mapCampaignApiItem(item)),
          total: response?.meta?.total_items ?? 0,
        };
      },
    });
  });

  const {
    rows: exportAdSetRows,
    onBeforeCapture: onBeforeAdSetPdfCapture,
    onAfterCapture: onAfterAdSetPdfCapture,
  } = useDashboardPdfRowOverride(paginatedAdSets, async () => {
    if (activeTab !== "adsets") {
      return paginatedAdSets;
    }
    return fetchAllPagesForExport({
      pageSize: TABLE_PAGE_SIZE,
      fetchPage: async (page, pageSize) => {
        const sort = toApiSort<MetaAdsAdsetSort>(
          adSetSort,
          ADSET_SORT_FIELD,
          "total_spend_desc"
        );
        const response = await metaAdsDashboardService.fetchAdsetKPI({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          lang: apiLang,
          page,
          page_size: pageSize,
          sort: sort ?? "total_spend_desc",
          search: debouncedSearchAdSets || null,
          platform: null,
        });
        return {
          rows: (response?.adsets ?? []).map((item, index) => mapAdsetApiItem(item, index)),
          total: response?.meta?.total_items ?? 0,
        };
      },
    });
  });

  const {
    rows: exportAdRows,
    onBeforeCapture: onBeforeAdsPdfCapture,
    onAfterCapture: onAfterAdsPdfCapture,
  } = useDashboardPdfRowOverride(paginatedAds, async () => {
    if (activeTab !== "ads") {
      return paginatedAds;
    }
    return fetchAllPagesForExport({
      pageSize: TABLE_PAGE_SIZE,
      fetchPage: async (page, pageSize) => {
        const sort = toApiSort<MetaAdsAdSort>(adsSort, AD_SORT_FIELD, "total_spend_desc");
        const response = await metaAdsDashboardService.fetchAdKPI({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          lang: apiLang,
          page,
          page_size: pageSize,
          sort: sort ?? "total_spend_desc",
          search: debouncedSearchAds || null,
          platform: null,
        });
        return {
          rows: (response?.ads ?? []).map((item, index) => mapAdApiItem(item, index)),
          total: response?.meta?.total_items ?? 0,
        };
      },
    });
  });

  const toggleCampaignExpanded = useCallback((campaignRowId: string) => {
    setExpandedCampaigns(current => {
      const next = new Set(current);
      if (next.has(campaignRowId)) {
        next.delete(campaignRowId);
      } else {
        next.add(campaignRowId);
      }
      return next;
    });
  }, []);

  const liveKpiMap = useMemo(() => {
    return liveKpis.reduce<Record<string, MetaAdsKpiItem>>((accumulator, item) => {
      const id = item?.id?.trim().toLowerCase();
      const title = item?.title?.trim().toLowerCase();
      if (id) {
        accumulator[id] = item;
      }
      if (title) {
        accumulator[title] = item;
      }
      return accumulator;
    }, {});
  }, [liveKpis]);

  const currencyPrefix = useMemo(
    () => extractCurrencyPrefixFromKpiItems(liveKpis),
    [liveKpis]
  );
  const formatMoney = useCallback(
    (
      value: number,
      options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
    ) => formatPaidMediaMoney(value, currencyPrefix, options),
    [currencyPrefix]
  );
  const formatMoneyShort = useCallback(
    (value: number) => formatPaidMediaMoneyShort(value, currencyPrefix),
    [currencyPrefix]
  );

  const numberLocale = useMemo(() => getNumberLocale(i18n.language), [i18n.language]);

  const exportCampaignsCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const sort = toApiSort<MetaAdsCampaignSort>(
        campaignSort,
        CAMPAIGN_SORT_FIELD,
        "total_spend_desc"
      );
      const responses = await Promise.all(
        Array.from({ length: campaignTotalPages }, (_, pageIndex) =>
          metaAdsDashboardService.fetchCampaignKPI({
            start_date: appliedStartDate,
            end_date: appliedEndDate,
            lang: apiLang,
            page: pageIndex + 1,
            page_size: TABLE_PAGE_SIZE,
            sort: sort ?? "total_spend_desc",
            search: debouncedSearchCampaigns || null,
            platform: null,
          })
        )
      );
      const headers = [
        t("metaAdsReport.campaignTable.campaign"),
        t("metaAdsReport.campaignTable.budgetCategory"),
        t("metaAdsReport.campaignTable.dailyBudget"),
        t("metaAdsReport.campaignTable.revenue"),
        t("metaAdsReport.campaignTable.spend"),
        t("metaAdsReport.campaignTable.impressions"),
        t("metaAdsReport.campaignTable.ctr"),
        t("metaAdsReport.campaignTable.cpc"),
        t("metaAdsReport.campaignTable.cpa"),
        t("metaAdsReport.campaignTable.roas"),
        t("metaAdsReport.campaignTable.convShort"),
        t("metaAdsReport.campaignTable.linkClicks"),
        t("metaAdsReport.campaignTable.campaignStartAt"),
        t("metaAdsReport.campaignTable.campaignStopAt"),
        t("metaAdsReport.campaignTable.campaignCreatedAt"),
      ];
      const rows: Array<Array<string | number | null | undefined>> = [];
      responses.flatMap(response => response?.campaigns ?? []).forEach(item => {
        const row = mapCampaignApiItem(item);
        rows.push([
          row.campaignName,
          row.budgetCategory ?? "",
          row.dailyBudget ?? "",
          row.revenue ?? "",
          row.spend ?? "",
          row.impressions ?? "",
          row.ctr !== null ? `${row.ctr.toFixed(2)}%` : "",
          row.cpc !== null ? formatMoneyShort(row.cpc) : "",
          row.cpa !== null ? formatMoneyShort(row.cpa) : "",
          row.roas !== null ? `${row.roas.toFixed(2)}x` : "",
          row.conversions ?? "",
          row.linkClicks ?? "",
          row.campaignStartAt ?? "",
          row.campaignStopAt ?? "",
          row.campaignCreatedAt ?? "",
        ]);
        (item.adsets ?? []).forEach((adset, adsetIndex) => {
          const adsetRow = mapAdsetApiItem(adset, adsetIndex);
          rows.push([
            `  ${adsetRow.name}`,
            adsetRow.budgetCategory ?? "",
            adsetRow.dailyBudget ?? "",
            adsetRow.revenue ?? "",
            adsetRow.spend,
            adsetRow.impressions,
            adsetRow.ctr !== null ? `${adsetRow.ctr.toFixed(2)}%` : "",
            adsetRow.cpc !== null ? formatMoneyShort(adsetRow.cpc) : "",
            adsetRow.cpa !== null ? formatMoneyShort(adsetRow.cpa) : "",
            `${adsetRow.roas.toFixed(2)}x`,
            adsetRow.conversions,
            adsetRow.linkClicks ?? "",
            adsetRow.adsetStartAt ?? "",
            adsetRow.adsetEndAt ?? "",
            adsetRow.adsetCreatedAt ?? "",
          ]);
        });
      });
      downloadCsv("campaigns.csv", headers, rows);
    } catch {
      window.alert("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [
    apiLang,
    appliedEndDate,
    appliedStartDate,
    campaignTotalPages,
    campaignSort,
    debouncedSearchCampaigns,
    formatMoneyShort,
    t,
  ]);

  const exportAdSetsCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const sort = toApiSort<MetaAdsAdsetSort>(adSetSort, ADSET_SORT_FIELD, "total_spend_desc");
      const responses = await Promise.all(
        Array.from({ length: adSetTotalPages }, (_, pageIndex) =>
          metaAdsDashboardService.fetchAdsetKPI({
            start_date: appliedStartDate,
            end_date: appliedEndDate,
            lang: apiLang,
            page: pageIndex + 1,
            page_size: TABLE_PAGE_SIZE,
            sort: sort ?? "total_spend_desc",
            search: debouncedSearchAdSets || null,
            platform: null,
          })
        )
      );
      const headers = [
        t("metaAdsReport.adSetTable.adSet"),
        t("metaAdsReport.adSetTable.budgetCategory"),
        t("metaAdsReport.adSetTable.dailyBudget"),
        t("metaAdsReport.adSetTable.spend"),
        t("metaAdsReport.adSetTable.impressions"),
        t("metaAdsReport.adSetTable.conversions"),
        t("metaAdsReport.adSetTable.ctr"),
        t("metaAdsReport.adSetTable.roas"),
        t("metaAdsReport.adSetTable.linkClicks"),
        t("metaAdsReport.adSetTable.adsetStartAt"),
        t("metaAdsReport.adSetTable.adsetEndAt"),
        t("metaAdsReport.adSetTable.adsetCreatedAt"),
      ];
      const rows = responses.flatMap(response => response?.adsets ?? []).map((item, index) => {
        const row = mapAdsetApiItem(item, index);
        return [
          row.name,
          row.budgetCategory ?? "",
          row.dailyBudget ?? "",
          formatMoney(row.spend),
          row.impressions,
          row.conversions,
          row.ctr !== null ? `${row.ctr.toFixed(2)}%` : "",
          `${row.roas.toFixed(2)}x`,
          row.linkClicks ?? "",
          row.adsetStartAt ?? "",
          row.adsetEndAt ?? "",
          row.adsetCreatedAt ?? "",
        ];
      });
      downloadCsv("adsets.csv", headers, rows);
    } catch {
      window.alert("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [
    adSetTotalPages,
    adSetSort,
    apiLang,
    appliedEndDate,
    appliedStartDate,
    debouncedSearchAdSets,
    formatMoney,
    t,
  ]);

  const exportAdsCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const sort = toApiSort<MetaAdsAdSort>(adsSort, AD_SORT_FIELD, "total_spend_desc");
      const responses = await Promise.all(
        Array.from({ length: adsTotalPages }, (_, pageIndex) =>
          metaAdsDashboardService.fetchAdKPI({
            start_date: appliedStartDate,
            end_date: appliedEndDate,
            lang: apiLang,
            page: pageIndex + 1,
            page_size: TABLE_PAGE_SIZE,
            sort: sort ?? "total_spend_desc",
            search: debouncedSearchAds || null,
            platform: null,
          })
        )
      );
      const headers = [
        t("metaAdsReport.adsTable.ad"),
        t("metaAdsReport.adsTable.dailyBudget"),
        t("metaAdsReport.adsTable.spend"),
        t("metaAdsReport.adsTable.impressions"),
        t("metaAdsReport.adsTable.conversions"),
        t("metaAdsReport.adsTable.ctr"),
        t("metaAdsReport.adsTable.cpc"),
        t("metaAdsReport.adsTable.cpa"),
        t("metaAdsReport.adsTable.roas"),
        t("metaAdsReport.adsTable.linkClicks"),
        t("metaAdsReport.adsTable.createdAtDate"),
        t("metaAdsReport.adsTable.createdAtTime"),
      ];
      const rows = responses.flatMap(response => response?.ads ?? []).map((item, index) => {
        const row = mapAdApiItem(item, index);
        return [
          row.name,
          row.dailyBudget ?? "",
          formatMoney(row.spend),
          row.impressions,
          row.conversions,
          `${row.ctr.toFixed(2)}%`,
          formatMoneyShort(row.cpc),
          formatMoneyShort(row.cpa),
          `${row.roas.toFixed(2)}x`,
          row.linkClicks ?? "",
          row.createdAtDate ?? "",
          row.createdAtTime ?? "",
        ];
      });
      downloadCsv("ads.csv", headers, rows);
    } catch {
      window.alert("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [
    adsTotalPages,
    adsSort,
    apiLang,
    appliedEndDate,
    appliedStartDate,
    debouncedSearchAds,
    formatMoney,
    formatMoneyShort,
    t,
  ]);

  const localKpiFallbackValues = useMemo((): Record<MetaKpiMetricId, string> => ({
    "total-spend": EMPTY_API_VALUE,
    impressions: EMPTY_API_VALUE,
    clicks: EMPTY_API_VALUE,
    conversions: EMPTY_API_VALUE,
    ctr: EMPTY_API_VALUE,
    cpc: EMPTY_API_VALUE,
    cpa: EMPTY_API_VALUE,
    roas: EMPTY_API_VALUE,
    "link-clicks": EMPTY_API_VALUE,
    "link-click-rate": EMPTY_API_VALUE,
    "cost-per-link-click": EMPTY_API_VALUE,
    purchases: EMPTY_API_VALUE,
    cpp: EMPTY_API_VALUE,
    "purchase-roas": EMPTY_API_VALUE,
    "link-click-through-rate": EMPTY_API_VALUE,
    "drop-off-impressions-clicks": EMPTY_API_VALUE,
    "drop-off-clicks-conversions": EMPTY_API_VALUE,
  }), []);

  const getTooltipForSuffix = useCallback(
    (suffix: MetaKpiTooltipSuffix): DefinitionTooltipInfo => {
      const description = t(`metaAdsReport.kpiTooltips.${suffix}.description`);
      const formula = t(`metaAdsReport.kpiTooltips.${suffix}.formula`, { defaultValue: "" });
      return formula ? { description, formula } : { description };
    },
    [t]
  );

  const getKpiTooltipForMetric = useCallback(
    (metricId: MetaKpiMetricId): DefinitionTooltipInfo =>
      getTooltipForSuffix(META_KPI_I18N_KEY[metricId]),
    [getTooltipForSuffix]
  );

  const getDisplayedKpiValue = useCallback(
    (metricId: MetaKpiMetricId) => {
      const liveItem = liveKpiMap[metricId];
      if (liveItem?.formatted_value) {
        return liveItem.formatted_value;
      }

      return localKpiFallbackValues[metricId];
    },
    [liveKpiMap, localKpiFallbackValues]
  );

  const getDisplayedKpiTitle = useCallback(
    (metricId: MetaKpiMetricId) => {
      const liveItem = liveKpiMap[metricId];
      if (liveItem?.title?.trim()) {
        return formatMetaKpiTitle(liveItem.title.trim());
      }
      const i18nKey = META_KPI_I18N_KEY[metricId];
      return t(`metaAdsReport.kpis.${i18nKey}`);
    },
    [liveKpiMap, t]
  );

  const metaKpiIcons = useMemo(() => {
    const labels =
      liveKpis.length > 0 ? liveKpis.map(item => item.title) : META_KPI_ICON_LABELS;
    return renderUniqueDashboardKpiLucideIcons(labels, 22, "metaAds");
  }, [liveKpis]);

  const getKpiTooltipForItem = useCallback(
    (kpi: MetaAdsKpiItem): DefinitionTooltipInfo => {
      const metricId = kpi.id?.trim().toLowerCase() as MetaKpiMetricId;
      if (metricId && META_KPI_I18N_KEY[metricId]) {
        return getKpiTooltipForMetric(metricId);
      }
      const titleSuffix = kpi.title ? matchTitleTooltipSuffix(kpi.title) : undefined;
      if (titleSuffix) {
        return getTooltipForSuffix(titleSuffix);
      }
      return { description: formatMetaKpiTitle(kpi.title ?? "") };
    },
    [getKpiTooltipForMetric, getTooltipForSuffix]
  );

  const toggleMetric = (metric: TrendMetricKey) => {
    setActiveTrendMetrics(current =>
      current.includes(metric)
        ? current.filter(item => item !== metric)
        : [...current, metric]
    );
  };

  const toggleCampaignSort = (key: CampaignSortKey) => {
    setCurrentCampaignPage(1);
    setCampaignSort(current => toggleTableSort(current, key));
  };
  const toggleAdSetSort = (key: AdSetSortKey) => {
    setCurrentAdSetPage(1);
    setAdSetSort(current => toggleTableSort(current, key));
  };
  const toggleAdsSort = (key: AdSortKey) => {
    setCurrentAdsPage(1);
    setAdsSort(current => toggleTableSort(current, key));
  };
  return (
    <section
      className={`paid-media-report${isPdfCapturing ? " paid-media-report--pdf-capture" : ""}`}
      ref={pdfContainerRef}
    >
      <div className="paid-media-report__header-block">
        <DashboardSectionHeader
          className="paid-media-report__section-header"
          divider={false}
          title={t("metaAdsReport.title")}
          actions={
            <>
              <div className="dashboard-section-header__control dashboard-section-header__control--wide">
                <OrganicDateRangePicker
                  startDate={draftStartDate}
                  endDate={draftEndDate}
                  onRangeChange={onApplyDates}
                />
              </div>
              <DashboardExportButton
                dashboardName="Meta Ads"
                dateRange={{ startDate: appliedStartDate, endDate: appliedEndDate }}
                containerRef={pdfContainerRef}
                disabled={isLoading || Boolean(dateRangeError)}
                onBeforeCapture={async () => {
                  setIsPdfCapturing(true);
                  if (activeTab === "campaigns") {
                    await onBeforeCampaignPdfCapture();
                  } else if (activeTab === "adsets") {
                    await onBeforeAdSetPdfCapture();
                  } else if (activeTab === "ads") {
                    await onBeforeAdsPdfCapture();
                  }
                }}
                onAfterCapture={() => {
                  onAfterCampaignPdfCapture();
                  onAfterAdSetPdfCapture();
                  onAfterAdsPdfCapture();
                  setIsPdfCapturing(false);
                }}
              />
            </>
          }
        />
        <div className="paid-media-report__subtitle-toolbar">
          <p className="dashboard-section-header__subtitle paid-media-report__header-subtitle">
            {t("metaAdsReport.subtitle")}
          </p>
        </div>
      </div>

      <div className="paid-media-report__content">
        {dateRangeError ? <DashboardErrorState message={dateRangeError} /> : null}
        {!dateRangeError && kpiApiError ? <DashboardErrorState message={kpiApiError} /> : null}
        {!dateRangeError && campaignApiError ? (
          <DashboardErrorState message={campaignApiError} />
        ) : null}
        {!dateRangeError && adSetApiError && activeTab === "adsets" ? (
          <DashboardErrorState message={adSetApiError} />
        ) : null}
        {!dateRangeError && adsApiError && activeTab === "ads" ? (
          <DashboardErrorState message={adsApiError} />
        ) : null}
        {!dateRangeError && segmentApiError ? (
          <DashboardErrorState message={segmentApiError} />
        ) : null}
        {!dateRangeError && trendsApiError ? (
          <DashboardErrorState message={trendsApiError} />
        ) : null}
        {isLoading ? (
          <div className="paid-media-report__loading">
            <LoadingSpinner compact />
          </div>
        ) : null}

        {!dateRangeError && !isLoading ? (
          <>
            <div data-pdf-block="narrative">
              <DashboardNarrativePanel
                narrative={metaNarrative}
                loading={narrativeLoading}
                aiSummary={aiSummary}
              />
            </div>
            <div data-pdf-block="kpi-cards">
            <MetaKpiGrid
              liveKpis={liveKpis}
              kpiIcons={metaKpiIcons}
              getKpiTooltipForItem={getKpiTooltipForItem}
              getKpiTooltipForMetric={getKpiTooltipForMetric}
              getDisplayedKpiTitle={getDisplayedKpiTitle}
              getDisplayedKpiValue={getDisplayedKpiValue}
              comparisonLabel={t("metaAdsReport.vsLastPeriod")}
              noTrendLabel={t("webAnalytics.kpi.trendUnavailable")}
              noTrendTooltip={t("webAnalytics.kpi.noEarlierData")}
              formulaTitle={t("webAnalytics.kpi.formula")}
            />
            </div>

            <div className="paid-media-overview-grid" data-pdf-block="trends-funnel">
              <MetaPerformanceTrendsPanel
                metaSectionHelp={metaSectionHelp}
                trendGranularity={trendGranularity}
                setTrendGranularity={setTrendGranularity}
                trendMetrics={trendMetrics}
                activeTrendMetrics={activeTrendMetrics}
                toggleMetric={toggleMetric}
                paginatedTrendData={paginatedTrendData}
                formatDateShortChart={formatDateShortChart}
                formatChartAxisCompactLocalized={formatChartAxisCompactLocalized}
                formatMoney={formatMoney}
                currentTrendPage={currentTrendPage}
                setCurrentTrendPage={setCurrentTrendPage}
                trendTotalPages={trendTotalPages}
              />

              <ConversionFunnelPanel
                title={t("metaAdsReport.sections.conversionFunnel")}
                tooltipInfo={metaSectionHelp("conversionFunnel")}
                steps={funnelSteps}
                formatMoney={formatMoney}
                numberLocale={numberLocale}
              />
            </div>

            <MetaChartsRow
              metaSectionHelp={metaSectionHelp}
              campaignBarData={campaignBarData}
              platformComparisonData={platformComparisonData}
              postTypeData={postTypeData}
              groupedBarLegendItems={groupedBarLegendItems}
              barSeriesLabels={barSeriesLabels}
              formatMoney={formatMoney}
              numberLocale={numberLocale}
              formatChartAxisCompactLocalized={formatChartAxisCompactLocalized}
            />

            <div className="paid-media-table-zone" data-pdf-block="ads-table" data-pdf-splittable>
              <div className="paid-media-tabs-bar paid-media-tabs-bar--attached">
                {(
                  [
                    ["campaigns", "overviewSubTabs.campaigns"],
                    ["adsets", "overviewSubTabs.adsets"],
                    ["ads", "overviewSubTabs.ads"],
                  ] as const
                ).map(([key, labelKey]) => (
                  <button
                    key={key}
                    type="button"
                    className={
                      activeTab === key
                        ? "paid-media-tabs-bar__tab paid-media-tabs-bar__tab--active"
                        : "paid-media-tabs-bar__tab"
                    }
                    onClick={() => setActiveTab(key as ActiveOverviewTab)}
                  >
                    {t(`metaAdsReport.${labelKey}`)}
                  </button>
                ))}
              </div>

            {isTableLoading ? (
              <div className="paid-media-report__loading">
                <LoadingSpinner compact />
              </div>
            ) : null}

            {!isTableLoading && activeTab === "campaigns" ? (
              <MetaCampaignsTable
                metaSectionHelp={metaSectionHelp}
                searchCampaigns={searchCampaigns}
                setSearchCampaigns={setSearchCampaigns}
                setCurrentCampaignPage={setCurrentCampaignPage}
                isExporting={isExporting}
                exportCampaignsCsv={exportCampaignsCsv}
                toggleCampaignSort={toggleCampaignSort}
                paginatedCampaigns={exportCampaignRows}
                expandedCampaigns={expandedCampaigns}
                toggleCampaignExpanded={toggleCampaignExpanded}
                formatMoney={formatMoney}
                formatMoneyShort={formatMoneyShort}
                currentCampaignPage={currentCampaignPage}
                campaignTotalPages={campaignTotalPages}
              />
            ) : null}

            {!isTableLoading && activeTab === "adsets" ? (
              <MetaAdSetsTable
                metaSectionHelp={metaSectionHelp}
                adSetPaginationMeta={adSetPaginationMeta}
                searchAdSets={searchAdSets}
                setSearchAdSets={setSearchAdSets}
                setCurrentAdSetPage={setCurrentAdSetPage}
                isExporting={isExporting}
                exportAdSetsCsv={exportAdSetsCsv}
                toggleAdSetSort={toggleAdSetSort}
                paginatedAdSets={exportAdSetRows}
                formatMoney={formatMoney}
                currentAdSetPage={currentAdSetPage}
                adSetTotalPages={adSetTotalPages}
              />
            ) : null}

            {!isTableLoading && activeTab === "ads" ? (
              <MetaAdsTable
                metaSectionHelp={metaSectionHelp}
                adsPaginationMeta={adsPaginationMeta}
                searchAds={searchAds}
                setSearchAds={setSearchAds}
                setCurrentAdsPage={setCurrentAdsPage}
                isExporting={isExporting}
                exportAdsCsv={exportAdsCsv}
                toggleAdsSort={toggleAdsSort}
                paginatedAds={exportAdRows}
                formatMoney={formatMoney}
                formatMoneyShort={formatMoneyShort}
                currentAdsPage={currentAdsPage}
                adsTotalPages={adsTotalPages}
              />
            ) : null}
            </div>

            {activeTab === "alerts" ? (
              <div data-pdf-block="alerts">
              <MetaAlertsPanel
                metaSectionHelp={metaSectionHelp}
                alerts={alerts}
                setAlerts={setAlerts}
                alertRuleLabel={alertRuleLabel}
                triggeredAlerts={triggeredAlerts}
                paginatedTriggeredAlerts={paginatedTriggeredAlerts}
                currentTriggeredAlertsPage={currentTriggeredAlertsPage}
                setCurrentTriggeredAlertsPage={setCurrentTriggeredAlertsPage}
                triggeredAlertsTotalPages={triggeredAlertsTotalPages}
                budgetRecommendations={budgetRecommendations}
                formatMoneyShort={formatMoneyShort}
              />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
};

export default MetaAdsReport;
